import {
  Activity,
  Check,
  Clock,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { type CSSProperties, useMemo } from 'react';

import { Separator } from '@/components/shadcn/ui/separator';
import { Switch } from '@/components/shadcn/ui/switch';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SchedulePeriodMap } from '@/types/commonConst';
import {
  WORKFLOW_COMPARE_DELTA,
  type WORKFLOW_REALTIME_BASIS,
  type WORKFLOW_SUBSCRIPTION_BASIS,
  WORKFLOW_TYPE,
  WorkflowIconMap,
  WorkflowPeriodicBasisDic,
  WorkflowSubscriptionBasisDic,
  WorkflowTypeDic,
  WorkspaceRealtimeOptionDic,
} from '@/types/workflowConst';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

type Props = Workflow & {
  onClick?: (noti: Workflow) => void;
  onToggle?: (alias: string, status: boolean) => void;
  style?: CSSProperties;
};

const WorkflowItem = (props: Props) => {
  const members = useWorkspaceStore((state) => state.currWorkspaceMembers);
  const updatedBy = members.find((mem) => mem.user_id === props.updated_by);
  const properties = useMemo(() => {
    switch (props.type) {
      case WORKFLOW_TYPE.PERIODIC:
        return <PeriodicWorkflowProperties {...props} />;
      case WORKFLOW_TYPE.REALTIME:
        return <RealtimeWorkflowProperties {...props} />;
      case WORKFLOW_TYPE.SUBSCRIPTION:
        return <SubscriptionWorkflowProperties {...props} />;
      case WORKFLOW_TYPE.SIGNAL_ALERT:
        return <SignalAlertWorkflowProperties {...props} />;
    }
  }, [props]);

  const legend = useMemo(() => {
    if (props.type === WORKFLOW_TYPE.PERIODIC) {
      if (!props.periodic.basis) return;
      return WorkflowPeriodicBasisDic[props.periodic.basis];
    }
    if (props.type === WORKFLOW_TYPE.REALTIME) return 'Feedback';
    if (props.type === WORKFLOW_TYPE.SUBSCRIPTION)
      return WorkflowSubscriptionBasisDic[props.subscription.basis as WORKFLOW_SUBSCRIPTION_BASIS] ?? 'Dashboard';
    if (props.type === WORKFLOW_TYPE.SIGNAL_ALERT) return 'Signal';
  }, [props]);

  return (
    <div
      className="flex cursor-pointer flex-col rounded-md border border-border p-6 hover:bg-accent"
      style={props.style}
      onClick={() => props.onClick?.(props)}
    >
      <div className="flex w-full items-center justify-between">
        <span className="text-sm font-semibold text-foreground" style={{ width: 'calc(100% - 80px)' }}>
          {props.title}
        </span>
        <Switch
          checked={props.enable}
          onCheckedChange={(checked) => {
            props.onToggle?.(props.alias, checked);
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <div className="h-2" />
      <div className="flex items-center gap-2">
        <div className="flex size-5 items-center justify-center rounded-full bg-muted text-xs text-muted-foreground [&_svg]:size-3">
          {WorkflowIconMap[props.type]}
        </div>
        <span className="text-sm text-muted-foreground">
          {WorkflowTypeDic[props.type]}
        </span>
        <Separator orientation="vertical" className="h-2.5" />
        <span className="text-sm text-muted-foreground">
          {legend}
        </span>
      </div>
      <div className="h-6" />
      <div className="flex items-center justify-between">
        {properties}
        <div className="flex items-center text-muted-foreground">
          <span className="text-sm">Modified by</span>
          <span className="ml-1 text-sm font-medium">{updatedBy?.first_name}</span>
          <span className="ml-2.5 text-sm">
            {getTimezoneDayjs(props.updated_at).format(DATE_FORMAT.readable)}
          </span>
        </div>
      </div>
    </div>
  );
};

const PeriodicWorkflowProperties = (props: PeriodicWorkflow) => {
  const value = useMemo(
    () =>
      props.periodic.percentage
        ? props.periodic.percentage
        : (props.periodic.numerical as number),
    [props.periodic.numerical, props.periodic.percentage],
  );
  const isIncrease = props.periodic.delta === WORKFLOW_COMPARE_DELTA.INCREASE;

  return (
    <div className="flex gap-6 text-sm font-medium text-muted-foreground">
      <div className="flex items-center gap-1.5">
        <Clock className="size-4" />
        {SchedulePeriodMap.get(props.periodic.schedule.period)}
      </div>
      <div className={`flex items-center gap-1.5 ${isIncrease ? 'text-green-600' : 'text-red-600'}`}>
        {isIncrease ? (
          <TrendingUp className="size-4" />
        ) : (
          <TrendingDown className="size-4" />
        )}
        <span>
          {value}
          {props.periodic.percentage && '%'}
        </span>
      </div>
    </div>
  );
};

const RealtimeWorkflowProperties = (props: RealtimeWorkflow) => {
  return (
    <div className="flex gap-6 text-sm font-medium text-muted-foreground">
      <div className="flex items-center gap-1.5">
        <Clock className="size-4" />
        {WorkflowTypeDic[props.type]}
      </div>
      <div className="flex items-center gap-1.5">
        <Check className="size-4" />
        {props.realtime.basis &&
          (WorkspaceRealtimeOptionDic[props.realtime.basis as WORKFLOW_REALTIME_BASIS] ?? 'Every feedback')}
      </div>
    </div>
  );
};

const SubscriptionWorkflowProperties = (props: SubscriptionWorkflow) => {
  return (
    <div className="flex gap-6 text-sm font-medium text-muted-foreground">
      <div className="flex items-center gap-1.5">
        <Clock className="size-4" />
        {SchedulePeriodMap.get(props.subscription.schedule.period)}
      </div>
      <div className="flex items-center gap-1.5">
        {props.subscription.target}
      </div>
    </div>
  );
};

const SignalAlertWorkflowProperties = (props: SignalAlertWorkflow) => {
  const signalCount = props.signal.signal_ids?.length ?? 0;
  return (
    <div className="flex gap-6 text-sm font-medium text-muted-foreground">
      <div className="flex items-center gap-1.5">
        <Activity className="size-4" />
        {signalCount} signal{signalCount !== 1 ? 's' : ''}
      </div>
      <div className="flex items-center gap-1.5">
        <Clock className="size-4" />
        {props.signal.duration_days} day{props.signal.duration_days !== 1 ? 's' : ''}
      </div>
    </div>
  );
};

export default WorkflowItem;
